import { z } from 'zod';

import {
  ACCESS_RUNTIME_DECISION_STATES,
  ACTION_POLICY_RISK_LEVELS,
  APPROVAL_MODES,
  APPROVAL_REQUEST_STATUS,
  TOOL_BROKER_RESULT_STATES,
  TOOL_INVOCATION_STATUS,
} from '../constants/enums';

const credentialsOmittedSchema = z.literal('not_included');
const nullableIdSchema = z.string().min(1).nullable();
const optionalNullableIdSchema = z.string().min(1).nullable().optional();

const SECRET_KEY_PATTERN = /(^|_|-)(authorization|api[_-]?key|access[_-]?token|refresh[_-]?token|bearer|oauth|password|secret|secret[_-]?ref|webhook[_-]?secret)(_|-|$)/i;

function isJsonText(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch (error) {
    if (error instanceof SyntaxError) return false;
    return false;
  }
}

function containsSecretKey(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  if (Array.isArray(value)) return value.some((item) => containsSecretKey(item));
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_KEY_PATTERN.test(key)) return true;
    if (containsSecretKey(child)) return true;
  }
  return false;
}

const sanitizedJsonTextSchema = z
  .string()
  .min(1)
  .refine(isJsonText, { message: 'must be valid JSON text' })
  .refine((value) => !containsSecretKey(JSON.parse(value) as unknown), {
    message: 'sanitized JSON must not contain secret-bearing keys',
  });

export const accessRuntimeActionRequestSchema = z.object({
  actor_id: z.string().min(1),
  identity_id: z.string().min(1).nullable().optional(),
  context_id: z.string().min(1).nullable().optional(),
  capability: z.string().min(1),
  resource_type: z.string().min(1).nullable().optional(),
  resource_id: z.string().min(1).nullable().optional(),
  risk_level: z.enum(ACTION_POLICY_RISK_LEVELS).default('low'),
  source_type: z.string().min(1),
  source_id: z.string().min(1),
  on_behalf_of_actor_id: z.string().min(1).nullable().optional(),
  payload_hash: z.string().min(1).nullable().optional(),
});
export type AccessRuntimeActionRequest = z.infer<typeof accessRuntimeActionRequestSchema>;

export const accessRuntimeAuthorizedViaSchema = z
  .object({
    type: z.enum(['role_assignment', 'direct_role_assignment', 'membership_role_assignment', 'delegation_grant', 'system']),
    id: z.string().min(1),
    membership_id: z.string().min(1).nullable(),
    on_behalf_of_actor_id: z.string().min(1).nullable(),
    capability: z.string().min(1).nullable(),
    context_id: z.string().min(1).nullable(),
  })
  .strict();
export type AccessRuntimeAuthorizedVia = z.infer<typeof accessRuntimeAuthorizedViaSchema>;

export const accessRuntimeDecisionPacketSchema = z
  .object({
    packet_type: z.literal('access_runtime'),
    version: z.literal('v3.access-runtime.1'),
    generated_at: z.string().min(1),
    actor_id: z.string().min(1),
    identity_id: nullableIdSchema,
    context_id: nullableIdSchema,
    capability: z.string().min(1),
    resource_type: nullableIdSchema,
    resource_id: nullableIdSchema,
    risk_level: z.enum(ACTION_POLICY_RISK_LEVELS),
    on_behalf_of_actor_id: nullableIdSchema,
    authorized_via: accessRuntimeAuthorizedViaSchema.nullable(),
    state: z.enum(ACCESS_RUNTIME_DECISION_STATES),
    approval_mode: z.enum(APPROVAL_MODES),
    required_approver_count: z.number().int().min(0),
    requires_distinct_approver: z.number().int().min(0).max(1),
    approval_request_id: nullableIdSchema,
    credential_grant_ids: z.array(z.string().min(1)),
    denial_reason: z.string().min(1).nullable(),
    redactions: z.array(z.string().min(1)),
    credentials: credentialsOmittedSchema,
    warnings: z.array(z.string().min(1)),
  })
  .strict()
  .superRefine((packet, ctx) => {
    const denied = packet.state === 'denied' || packet.state === 'forbidden';
    if (denied && !packet.denial_reason) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'denied decisions require a denial reason' });
    }
    if (!denied && packet.denial_reason) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'non-denied decisions must not carry a denial reason' });
    }
  });
export type AccessRuntimeDecisionPacket = z.infer<typeof accessRuntimeDecisionPacketSchema>;

export const approvalGatePacketSchema = z
  .object({
    packet_type: z.literal('approval_gate'),
    version: z.literal('v3.approval-gate.1'),
    generated_at: z.string().min(1),
    access: accessRuntimeDecisionPacketSchema,
    approval_request: z
      .object({
        id: z.string().min(1),
        source_type: z.string().min(1),
        source_id: z.string().min(1),
        capability: z.string().min(1),
        context_id: nullableIdSchema,
        requested_by_actor_id: z.string().min(1),
        approval_mode: z.enum(APPROVAL_MODES),
        required_approver_count: z.number().int().min(0),
        requires_distinct_approver: z.number().int().min(0).max(1),
        status: z.enum(APPROVAL_REQUEST_STATUS),
        idempotency_key: z.string().min(1),
        payload_hash: z.string().min(1),
      })
      .strict(),
    credentials: credentialsOmittedSchema,
  })
  .strict();
export type ApprovalGatePacket = z.infer<typeof approvalGatePacketSchema>;

export const toolBrokerInvocationRequestSchema = z.object({
  actor_id: z.string().min(1),
  identity_id: z.string().min(1).nullable().optional(),
  context_id: z.string().min(1).nullable().optional(),
  capability: z.string().min(1),
  tool_name: z.string().min(1),
  requested_action: z.string().min(1),
  subject_type: z.string().min(1),
  subject_id: z.string().min(1),
  request_hash: z.string().min(1),
  resource_type: z.string().min(1).nullable().optional(),
  resource_id: z.string().min(1).nullable().optional(),
  risk_level: z.enum(ACTION_POLICY_RISK_LEVELS).default('low'),
  source_type: z.string().min(1),
  source_id: z.string().min(1),
  on_behalf_of_actor_id: z.string().min(1).nullable().optional(),
  sanitized_request_json: sanitizedJsonTextSchema.default('{}'),
  correlation_id: optionalNullableIdSchema,
  causation_id: optionalNullableIdSchema,
  agent_run_id: z.string().min(1).nullable().optional(),
});
export type ToolBrokerInvocationRequest = z.infer<typeof toolBrokerInvocationRequestSchema>;

export const toolBrokerInvocationPacketSchema = z
  .object({
    packet_type: z.literal('tool_broker_invocation'),
    version: z.literal('v3.tool-broker.1'),
    generated_at: z.string().min(1),
    access: accessRuntimeDecisionPacketSchema,
    invocation: z
      .object({
        id: z.string().min(1),
        agent_run_id: z.string().min(1),
        actor_id: z.string().min(1),
        on_behalf_of_actor_id: nullableIdSchema,
        authorized_via_type: z.string().min(1).nullable(),
        authorized_via_id: z.string().min(1).nullable(),
        context_id: nullableIdSchema,
        tool_name: z.string().min(1),
        capability: z.string().min(1),
        requested_action: z.string().min(1),
        approval_mode: z.enum(APPROVAL_MODES),
        approval_request_id: nullableIdSchema,
        credential_grant_id: nullableIdSchema,
        subject_type: z.string().min(1),
        subject_id: z.string().min(1),
        request_hash: z.string().min(1),
        idempotency_key: z.string().min(1),
        sanitized_request_json: sanitizedJsonTextSchema,
        sanitized_result_json: sanitizedJsonTextSchema.nullable(),
        status: z.enum(TOOL_INVOCATION_STATUS),
        error_class: z.string().min(1).nullable(),
      })
      .strict(),
    result_state: z.enum(TOOL_BROKER_RESULT_STATES),
    denial_reason: z.string().min(1).nullable(),
    credentials: credentialsOmittedSchema,
    warnings: z.array(z.string().min(1)),
  })
  .strict()
  .superRefine((packet, ctx) => {
    if (packet.result_state === 'invoked' && !packet.invocation.credential_grant_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'invoked tools require credential grant lineage',
        path: ['invocation', 'credential_grant_id'],
      });
    }
  });
export type ToolBrokerInvocationPacket = z.infer<typeof toolBrokerInvocationPacketSchema>;
